import React from 'react';
import {
    ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
    PieChart, Pie, Cell, Legend,
} from 'recharts';
import { Inventory2, Payments, Sell } from '@mui/icons-material';
import StatCard from './StatCard';
import ChartCard from './ChartCard';
import TableCard from './TableCard';
import ChartTooltip from './ChartTooltip';
import StatusChip from '../shared/StatusChip';
import { COLORS } from '../shared/tokens';
import { gbp, gbpCompact, shortWeek, SERIES, CATEGORY_PALETTE } from './format';
import { formatDisplayDate } from '../../../helper';

export default function WarehouseDashboard({ data }) {
    const { kpis, trend = [], stockByType = [], recentSales = [] } = data;
    const spark = trend.map((t) => ({ v: t.amt }));
    const pie = stockByType.map((s) => ({ name: s.name, value: s.count }));

    return (
        <>
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 16 }}>
                <StatCard label="Available Stock" value={kpis.availableStock} sub="items in stock" accent={COLORS.primary} icon={<Inventory2 fontSize="small" />} />
                <StatCard label="Stock Value" value={gbpCompact(kpis.stockValue)} sub="at buy rate" accent="#15803d" icon={<Payments fontSize="small" />} />
                <StatCard label="8-Week Sales" value={gbpCompact(kpis.sales8)} sub={`through ${kpis.latestWeekCode}`} accent="#2563eb" icon={<Sell fontSize="small" />} spark={spark} />
                <StatCard label="Latest Week Sales" value={gbpCompact(kpis.latestWeek)} sub={kpis.latestWeekCode} accent="#a16207" icon={<Sell fontSize="small" />} />
            </div>

            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 16 }}>
                <ChartCard title="Warehouse Sales" subtitle="net amount per week" flex="2 1 460px" legend={[{ label: 'Sales', color: SERIES.cash }]}>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={trend} margin={{ top: 6, right: 8, left: -12, bottom: 0 }} barSize={22}>
                            <CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} vertical={false} />
                            <XAxis dataKey="week" tickFormatter={shortWeek} tick={{ fill: COLORS.textDim, fontSize: 11 }} axisLine={false} tickLine={false} />
                            <YAxis tickFormatter={(v) => gbpCompact(v)} tick={{ fill: COLORS.textDim, fontSize: 11 }} axisLine={false} tickLine={false} width={54} />
                            <Tooltip cursor={{ fill: COLORS.primaryTintSoft }} content={<ChartTooltip week />} />
                            <Bar dataKey="amt" name="Sales" fill={SERIES.cash} radius={[3, 3, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </ChartCard>

                <ChartCard title="Stock by Type" subtitle="available items" flex="1 1 300px">
                    {pie.length === 0 ? (
                        <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: COLORS.placeholder, fontSize: 13 }}>
                            No stock available
                        </div>
                    ) : (
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie data={pie} dataKey="value" nameKey="name" innerRadius={45} outerRadius={72} paddingAngle={2}>
                                    {pie.map((p, i) => <Cell key={i} fill={CATEGORY_PALETTE[i % CATEGORY_PALETTE.length]} />)}
                                </Pie>
                                <Tooltip content={<ChartTooltip prefix="" />} />
                                <Legend iconType="circle" wrapperStyle={{ fontSize: 11 }} />
                            </PieChart>
                        </ResponsiveContainer>
                    )}
                </ChartCard>
            </div>

            <TableCard
                title="Recent Warehouse Sales"
                columns={[
                    { key: 'week', label: 'Week', render: (r) => shortWeek(r.week) },
                    { key: 'customer', label: 'Customer', render: (r) => r.customer || 'N/A' },
                    { key: 'toType', label: 'To', render: (r) => <StatusChip label={r.toType || 'N/A'} /> },
                    { key: 'date', label: 'Date', render: (r) => formatDisplayDate(r.date) || r.date },
                    { key: 'amount', label: 'Amount', align: 'right', render: (r) => gbp(r.amount) },
                ]}
                rows={recentSales}
                emptyLabel="No warehouse sales recorded yet"
            />
        </>
    );
}
